// Conversation identity is deliberately store-first. A phone number is not a
// tenant identity: the same customer can legitimately have a conversation in
// more than one store.
export function normalizedConversationPhone(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  return digits.startsWith("55") && digits.length > 11 ? digits.slice(2) : digits;
}

export function conversationKey(storeId, phone) {
  const store = Number(storeId);
  const normalizedPhone = normalizedConversationPhone(phone);
  return Number.isFinite(store) && store > 0 && normalizedPhone
    ? `${store}:${normalizedPhone}`
    : null;
}

export const conversationReadKey = conversationKey;

export function conversationTarget(moduleId, storeId, phone) {
  const key = conversationKey(storeId, phone);
  return key ? { moduleId, storeId:Number(storeId), phone:normalizedConversationPhone(phone), conversationKey:key } : null;
}

export function recordMatchesConversationTarget(record, target, phoneField = "phone") {
  const phoneFields = Array.isArray(phoneField) ? phoneField : [phoneField];
  if (target?.recordId != null) {
    const recordStoreId = Number(target.moduleId === "leads"
      ? record?.lojaAtual ?? record?.storeId
      : record?.storeId);
    return String(record?.id || "") === String(target.recordId)
      && recordStoreId > 0
      && recordStoreId === Number(target.storeId)
      && phoneFields.some(field =>
        normalizedConversationPhone(record?.[field]) === normalizedConversationPhone(target.phone));
  }
  return !!target && phoneFields.some(field =>
    conversationKey(record?.storeId, record?.[field]) === conversationKey(target.storeId, target.phone));
}

export function conversationMessagePath(storeId, phone, messageId) {
  const store = Number(storeId);
  const normalizedPhone = normalizedConversationPhone(phone);
  if (!Number.isFinite(store) || store <= 0 || !normalizedPhone || !messageId) return null;
  return `arflow_conversations/${store}/${normalizedPhone}/${messageId}`;
}

// The API may return either { conversations: [{ storeId, phone, messages }] }
// or the RTDB-shaped { [storeId]: { [phone]: { [messageId]: message } } }.
// Messages from old global nodes have no authenticated parent scope and are
// intentionally ignored. A message must carry the same storeId as its scoped
// API record; legacy messages without storeId are never silently adopted.
export function scopedConversationCache(payload, allowedStoreIds = []) {
  const allowed = new Set((allowedStoreIds || []).map(Number).filter(id => id > 0));
  const out = {};
  const add = (storeId, phone, messages) => {
    const store = Number(storeId);
    const key = conversationKey(store, phone);
    if (!key || !allowed.has(store) || !messages || typeof messages !== "object") return;
    const filtered = Object.fromEntries(Object.entries(messages)
      .filter(([, message]) => message && typeof message === "object"
        && (!message.storeId || Number(message.storeId) === store))
      .map(([id, message]) => [id, { ...message, storeId:store }]));
    if (Object.keys(filtered).length) out[key] = filtered;
  };

  if (Array.isArray(payload?.conversations)) {
    payload.conversations.forEach(record => add(record?.storeId, record?.phone, record?.messages));
  } else if (payload?.conversations && typeof payload.conversations === "object") {
    Object.entries(payload.conversations).forEach(([storeId, phones]) =>
      Object.entries(phones || {}).forEach(([phone, messages]) => add(storeId, phone, messages)));
  } else if (payload && typeof payload === "object") {
    Object.entries(payload).forEach(([storeId, phones]) =>
      Object.entries(phones || {}).forEach(([phone, messages]) => add(storeId, phone, messages)));
  }
  return out;
}

export function messagesForConversation(cache, storeId, phone) {
  const key = conversationKey(storeId, phone);
  return key ? (cache?.[key] || {}) : {};
}

// Cards read the latest authenticated API snapshot, not accumulated chat
// history. Without a snapshot (e.g. demo), retain the existing scoped cache.
export function replyMessagesForConversation(cache, storeId, phone) {
  const key = conversationKey(storeId, phone);
  return key && cache?.__latestEvidence
    ? (cache.__latestEvidence[key] || {}) : messagesForConversation(cache, storeId, phone);
}

export function replyMessagesForLeadCard(cache, leadMessages, leadId, storeId, phone) {
  const key = conversationKey(storeId, phone);
  // Once the scoped endpoint responds, absence from its latest index must not
  // resurrect an older inbound from a Firebase source on the Central card.
  if (cache?.__latestEvidence && key) {
    return Object.values(cache.__latestEvidence[key] || {});
  }
  return Object.values({
    ...(leadMessages?.[leadId] || {}),
    ...messagesForConversation(cache, storeId, phone),
  });
}

export function preserveLatestConversationEvidence(previous, sourceSnapshot) {
  if (!previous?.__latestEvidence) return sourceSnapshot;
  return { ...sourceSnapshot, __latestEvidence:previous.__latestEvidence };
}

// A leitura pontual do painel confirma o snapshot canônico mais recente, mas
// não pode apagar mensagens que já chegaram pelo cache escopado da mesma
// conversa. Isso é especialmente importante para mídia: texto pode existir
// duplicado em outra fonte, enquanto a foto/áudio/vídeo tem uma única cópia.
// Em colisões de ID, o snapshot direto é a autoridade.
export function mergeConversationMessages(cachedMessages, directMessages) {
  return {
    ...(cachedMessages && typeof cachedMessages === "object" ? cachedMessages : {}),
    ...(directMessages && typeof directMessages === "object" ? directMessages : {}),
  };
}

export function mergeIndividualConversationPayload(cache, payload, requestedStoreId, requestedPhone) {
  const identity = conversationKey(requestedStoreId, requestedPhone);
  if (!identity || conversationKey(payload?.storeId, payload?.phone) !== identity) return cache || {};
  const scoped = scopedConversationCache({
    conversations:[{
      storeId:payload.storeId,
      phone:payload.phone,
      messages:payload.messages || {},
    }],
  }, [requestedStoreId]);
  return {
    ...(cache || {}),
    [identity]:mergeConversationMessages(cache?.[identity], scoped[identity]),
  };
}

// O frontend cria a bolha antes da resposta do servidor. Se o resolvedor
// canônico corrigir o canal de uma conversa transferida, move essa bolha para
// a chave confirmada em vez de deixá-la invisível no cache da loja responsável.
export function relocateConversationMessage(cache, {
  fromStoreId,
  toStoreId,
  phone,
  messageId,
  message,
}) {
  const fromKey = conversationKey(fromStoreId, phone);
  const toKey = conversationKey(toStoreId, phone);
  if (!fromKey || !toKey || !messageId) return cache || {};

  const previous = cache || {};
  const fromBucket = { ...(previous[fromKey] || {}) };
  const sourceMessage = fromBucket[messageId] || message || {};
  const next = { ...previous };

  delete fromBucket[messageId];
  if (Object.keys(fromBucket).length) next[fromKey] = fromBucket;
  else if (fromKey !== toKey) delete next[fromKey];

  next[toKey] = {
    ...(next[toKey] || {}),
    [messageId]: {
      ...sourceMessage,
      ...(message || {}),
      storeId:Number(toStoreId),
    },
  };
  return next;
}

export function conversationSearchMatches(record, query) {
  const normalizedQuery = normalizedConversationPhone(query);
  return !!normalizedQuery && normalizedConversationPhone(record?.phone).includes(normalizedQuery);
}

export function scopedConversationSearch(records, query, phoneField = "phone") {
  const found = new Map();
  for (const record of records || []) {
    if (!conversationSearchMatches({ phone:record?.[phoneField] }, query)) continue;
    const key = conversationKey(record?.storeId, record?.[phoneField]);
    if (key && !found.has(key)) found.set(key, { ...record, conversationKey:key });
  }
  return [...found.values()];
}